import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, untracked } from "@angular/core";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatCard, MatCardContent } from "@angular/material/card";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatDialog } from "@angular/material/dialog";
import { MatDivider } from "@angular/material/divider";
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatInput } from "@angular/material/input";
import { LOCKED_PROPERTY_KEYS, isLockedPropertyKey } from "../../shared/model/feature-properties.constants";
import { DataDetailDialogComponent } from "../../ai-components/chat/components/content/shared/data-detail-dialog/data-detail-dialog.component";
import { detectPropertyValueType, type PropertyRow } from "./feature-properties-editor.types";

/**
 * Displays a feature's (or a FeatureCollection's) properties as a readable key/value list,
 * with optional inline editing: change values, add new keys, and delete non-locked keys.
 */
@Component({
  selector: "lib-feature-properties-editor",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButton,
    MatIconButton,
    MatCard,
    MatCardContent,
    MatCheckbox,
    MatDivider,
    MatFormField,
    MatLabel,
    MatIcon,
    MatInput,
  ],
  templateUrl: "./feature-properties-editor.component.html",
  styleUrl: "./feature-properties-editor.component.scss",
})
export class FeaturePropertiesEditorComponent {
  readonly properties = input.required<Record<string, unknown>>();
  readonly editable = input<boolean>(false);
  readonly lockedKeys = input<string[]>(LOCKED_PROPERTY_KEYS);

  readonly save = output<Record<string, unknown>>();

  private readonly dialog = inject(MatDialog);

  protected readonly isEditing = signal(false);
  protected readonly rows = signal<PropertyRow[]>([]);
  private readonly originalRows = signal<PropertyRow[]>([]);

  protected readonly dirty = computed(() => {
    const current = this.rows();
    const original = this.originalRows();
    if (current.length !== original.length) return true;
    return current.some((row, i) => row.key !== original[i].key || row.value !== original[i].value);
  });

  constructor() {
    effect(() => {
      const props = this.properties();
      const locked = this.lockedKeys();
      untracked(() => this.resetRows(props, locked));
    });
  }

  protected toggleEditOrSave(): void {
    if (this.isEditing()) {
      this.onSave();
    } else {
      this.startEdit();
    }
  }

  protected viewJson(value: unknown, key: string): void {
    this.dialog.open(DataDetailDialogComponent, {
      data: { data: { type: "json", json: value }, title: key },
      width: "90vw",
      maxWidth: "800px",
      maxHeight: "80vh",
    });
  }

  protected displayValue(value: unknown): string {
    if (value === null || value === undefined) return "—";
    return typeof value === "object" ? JSON.stringify(value) : String(value);
  }

  protected startEdit(): void {
    this.isEditing.set(true);
  }

  protected cancelEdit(): void {
    this.resetRows(this.properties(), this.lockedKeys());
  }

  protected addRow(): void {
    this.rows.update((rows) => [...rows, { key: "", value: "", type: "string", locked: false, isNew: true }]);
  }

  protected removeRow(index: number): void {
    this.rows.update((rows) => rows.filter((_, i) => i !== index));
  }

  protected updateKey(index: number, key: string): void {
    this.rows.update((rows) => rows.map((row, i) => (i === index ? { ...row, key } : row)));
  }

  /** Updates a row's raw text input value. Coercion back to the row's original type happens on save. */
  protected updateValue(index: number, value: string): void {
    this.rows.update((rows) => rows.map((row, i) => (i === index ? { ...row, value } : row)));
  }

  protected updateBooleanValue(index: number, value: boolean): void {
    this.rows.update((rows) => rows.map((row, i) => (i === index ? { ...row, value } : row)));
  }

  protected onSave(): void {
    const properties: Record<string, unknown> = {};
    this.rows().forEach((row) => {
      const key = row.key.trim();
      if (key) properties[key] = this.coerceValue(row);
    });
    this.save.emit(properties);
    this.originalRows.set(this.rows());
    this.isEditing.set(false);
  }

  /** Casts an edited row's value back to its original runtime type, so editing can never change a property's type. */
  private coerceValue(row: PropertyRow): unknown {
    switch (row.type) {
      case "number": {
        if (typeof row.value === "number") return row.value;
        const parsed = Number(row.value);
        if (row.value === "" || Number.isNaN(parsed)) {
          console.error("FeaturePropertiesEditorComponent: invalid number for", row.key, row.value);
          return row.value;
        }
        return parsed;
      }
      case "boolean":
        return typeof row.value === "boolean" ? row.value : row.value === "true";
      case "object":
      case "array":
        // Not editable inline — value is preserved untouched.
        return row.value;
      default:
        return row.value;
    }
  }

  private resetRows(properties: Record<string, unknown>, lockedKeys: string[]): void {
    const rows = Object.entries(properties ?? {}).map(([key, value]) => ({
      key,
      value,
      type: detectPropertyValueType(value),
      locked: isLockedPropertyKey(key, lockedKeys),
      isNew: false,
    }));
    this.rows.set(rows);
    this.originalRows.set(rows);
    this.isEditing.set(false);
  }
}
